import { useDropzone } from "react-dropzone";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

type LogoUploaderProps = {
  logo: string; // URL/base64 de l'image
  onDrop: (acceptedFiles: File[]) => void;
  title: string;
  required?: boolean;
  error?: boolean;
  onRemove?: () => void; // <-- nouvelle prop (le parent mettra logo="")
  disableRemove?: boolean; // optionnel
};

export function LogoUploader({
  logo,
  onDrop,
  title,
  required = false,
  error = false,
  onRemove,
  disableRemove = false,
}: LogoUploaderProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        <Box
          {...getRootProps({ className: "dropzone" })}
          sx={{
            textAlign: "center",
            p: 2,
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "#cccccc",
            borderRadius: 2,
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body2" color="textSecondary">
            Faites glisser une image ici, ou cliquez pour sélectionner une image
          </Typography>
        </Box>

        {required && error && (
          <Typography color="error" variant="body2" mt={1}>
            L'image est obligatoire
          </Typography>
        )}

        {logo && (
          <Grid mt={2}>
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <CardMedia
                component="img"
                image={logo}
                alt={title}
                sx={{
                  maxHeight: 200,
                  width: "auto",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              />

              {onRemove && (
                <Tooltip title="Supprimer l'image">
                  <span>
                    <IconButton
                      onClick={onRemove}
                      disabled={disableRemove}
                      size="small"
                      aria-label="Supprimer l'image"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "background.paper",
                        boxShadow: 1,
                        "&:hover": { bgcolor: "error.main", color: "white" },
                      }}
                    >
                      <DeleteForeverIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}
